import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { BookOpen, BarChart3, User, Sparkles, Menu } from 'lucide-react';
import { getDailyVerse } from '../data/bibleBooks';
import { getStatistics, getCompletedBooksCount } from '../utils/localStorage';
import { isUserLoggedIn } from '../utils/auth';
import { supabase } from '../lib/supabase';
import { SideMenu } from './SideMenu';
import { SettingsMenu } from './SettingsMenu';
import { DonateButton } from './DonateButton';
import { WelcomeModal } from './WelcomeModal';
import axios from 'axios';


export const HomePage = () => {
  const navigate = useNavigate();
  const dailyVerse = getDailyVerse();
  const stats = getStatistics();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [welcomeUserName, setWelcomeUserName] = useState('');



  useEffect(() => {
    const fetchProfile = async () => {
      const user = await supabase.auth.getUser();
      if (user?.data?.user) {

        const { data, error } = await supabase
          .from('profiles')
          .select('id, full_name, avatar_url, high_score, total_score, games_played, total_verses_revealed, books_completed')
          .eq('id', user.data.user.id)
          .single();

        if (data && !error) {
          setUserProfile(data);

          // Sync statistics from Supabase to LocalStorage
          const remoteStats = {
            totalScore: data.total_score || 0,
            totalVersesRevealed: data.total_verses_revealed || 0,
            gamesPlayed: data.games_played || 0,
            booksCompleted: data.books_completed || []
          };

          import('../utils/localStorage').then(mod => {
            const localStats = mod.getStatistics();
            const mergedStats = {
              ...localStats,
              totalScore: Math.max(localStats.totalScore || 0, remoteStats.totalScore),
              totalVersesRevealed: Math.max(localStats.totalVersesRevealed || 0, remoteStats.totalVersesRevealed),
              gamesPlayed: Math.max(localStats.gamesPlayed || 0, remoteStats.gamesPlayed),
              booksCompleted: Array.isArray(remoteStats.booksCompleted)
                ? [...new Set([...(localStats.booksCompleted || []), ...remoteStats.booksCompleted])]
                : (localStats.booksCompleted || [])
            };
            mod.setStatistics(mergedStats);
            if (data.high_score !== undefined) {
              mod.setHighScore(data.high_score, true);
            }
          });
        }


        // Check if welcome is needed
        try {
          const { data: welcomeData } = await axios.post('/api/auth/check-welcome', {
            userId: user.data.user.id
          });

          if (welcomeData?.shouldSend) {
            const userName = user.data.user.user_metadata?.full_name || user.data.user.email.split('@')[0];

            // Enviar notificación de bienvenida al backend
            await axios.post('/api/auth/welcome', {
              userId: user.data.user.id,
              email: user.data.user.email,
              name: userName
            });

            // Mostrar modal de bienvenida
            setWelcomeUserName(userName);
            setShowWelcomeModal(true);
          }
        } catch (err) {
          console.error('Error checking welcome status:', err);
        }

      }
    };
    fetchProfile();
  }, []);

  const handlePlayClick = async () => {
    const isLoggedIn = await isUserLoggedIn();

    if (isLoggedIn) {
      // User is logged in, go to book selection
      navigate('/select-book');
    } else {
      // User is not logged in, check if they have a session
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        // User exists but not logged in, go to login
        navigate('/login');
      } else {
        // New user, go to register
        navigate('/register');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20">
      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        userName={welcomeUserName}
      />

      {/* Side Menu */}
      <SideMenu open={menuOpen} onOpenChange={setMenuOpen} />

      {/* Header */}
      <header className="flex items-center justify-between p-4 md:p-6">
        <button
          onClick={() => setMenuOpen(true)}
          className="p-2 hover:bg-secondary rounded-lg interactive"
        >
          <Menu className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-3">
          <DonateButton />
          <SettingsMenu />
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 pb-24 mt-8">
        {/* Logo/Title with floating tetris piece */}
        <div className="text-center mb-12 relative">
          <div className="inline-block relative">
            {/* Decorative floating tetris pieces */}
            <div className="absolute -top-8 -right-8 w-12 h-12 bg-primary rounded-lg float opacity-80"></div>
            <div className="absolute -top-4 -left-12 w-8 h-8 bg-accent rounded opacity-60 float" style={{ animationDelay: '0.5s' }}></div>

            <div className="shadow-2xl inline-block overflow-hidden rounded-[32px]">
              <img
                src="/logo.png"
                alt="Tetris Bíblico Logo"
                className="w-32 h-32 md:w-40 md:h-40 object-cover"
              />
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-black mt-6 mb-3 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Tetris Bíblico
          </h1>
          <p className="text-muted-foreground text-lg">
            Construye tu fe,
            <br />
            bloque a bloque
          </p>
        </div>

        {/* Daily Verse Card */}
        <Card className="p-6 mb-8 bg-gradient-to-br from-card to-secondary/10 border-2 border-primary/20 shadow-lg">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-primary" />
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Versículo Diario
            </h2>
          </div>
          <p className="text-lg md:text-xl italic leading-relaxed mb-3">
            "{dailyVerse.text}"
          </p>
          <p className="text-right text-sm font-medium text-muted-foreground">
            — {dailyVerse.reference}
          </p>
        </Card>

        <div className="grid grid-cols-3 gap-3 mb-8">
          {/* Box 1: Partidas Jugadas */}
          <Card className="p-4 text-center bg-gradient-to-br from-white to-gray-200 border-2 border-gray-300 rounded-2xl shadow-[inset_0_2px_4px_rgba(255,255,255,0.8),0_4px_8px_rgba(0,0,0,0.1)] transition-transform hover:scale-[1.02]">
            <div className="text-[9px] font-black uppercase tracking-tight text-gray-500 leading-tight mb-2 h-4 flex items-center justify-center">
              Partidas Jugadas
            </div>
            <div className="text-2xl font-black text-black">
              {userProfile ? userProfile.games_played : stats.gamesPlayed}
            </div>
          </Card>

          {/* Box 2: Libros Completados */}
          <Card className="p-4 text-center bg-gradient-to-br from-accent/30 to-accent/10 border-2 border-accent/30 rounded-2xl shadow-[inset_0_2px_4px_rgba(255,255,255,0.6),0_4px_8px_rgba(0,0,0,0.1)] transition-transform hover:scale-[1.02]">
            <div className="text-[9px] font-black uppercase tracking-tight text-gray-600 leading-tight mb-2 h-4 flex items-center justify-center">
              Libros Completados
            </div>
            <div className="text-2xl font-black text-black">
              {userProfile ? (Array.isArray(userProfile.books_completed) ? userProfile.books_completed.length : 0) : getCompletedBooksCount()}/66
            </div>
          </Card>

          {/* Box 3: Puntuación Total */}
          <Card className="p-4 text-center bg-gradient-to-br from-yellow-100 to-yellow-200 border-2 border-yellow-300 rounded-2xl shadow-[inset_0_2px_4px_rgba(255,255,255,0.8),0_4px_8px_rgba(0,0,0,0.1)] transition-transform hover:scale-[1.02]">
            <div className="text-[9px] font-black uppercase tracking-tight text-gray-600 leading-tight mb-2 h-4 flex items-center justify-center">
              Puntuación Total
            </div>
            <div className="text-2xl font-black text-black">
              {(() => {
                const cloudScore = userProfile?.total_score || 0;
                const localScore = stats.totalScore || 0;
                const score = Math.max(cloudScore, localScore);
                return score || 0;
              })()}
            </div>
          </Card>
        </div>

        {/* Main CTA */}
        <Button
          onClick={handlePlayClick}
          size="lg"
          className="w-full h-16 text-lg font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-full shadow-lg pulse-glow mb-4"
        >
          <span className="mr-3 text-xl">▶</span>
          JUGAR
        </Button>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-2xl">
        <div className="max-w-2xl mx-auto px-4 py-4 grid grid-cols-4 gap-2">
          <button
            onClick={() => navigate('/')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg bg-primary text-primary-foreground"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full">
              <div className="w-5 h-5 bg-current rounded"></div>
            </div>
            <span className="text-xs font-semibold">Inicio</span>
          </button>
          <button
            onClick={() => navigate('/collection')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary interactive"
          >
            <BookOpen className="w-5 h-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Colección</span>
          </button>
          <button
            onClick={() => navigate('/ranking')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary interactive"
          >
            <BarChart3 className="w-5 h-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Ranking</span>
          </button>
          <button
            onClick={() => navigate('/profile')}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary interactive"
          >
            <User className="w-5 h-5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">Perfil</span>
          </button>
        </div>
      </nav>
    </div>
  );
};